import { FiltersType, IHousingListResponse } from '@/types';
import { ReferenceLocation } from '@/types/referenceLocation';

type FilterData = IHousingListResponse['filterData'];

/**
 * Returns only the deepest selected codes — drops a code if any other selected
 * code starts with it followed by a dot (i.e. is a child of it).
 *
 * Example: ["AT", "AT.3", "AT.3.306"] → ["AT.3.306"]
 * Example: ["AT.1", "AT.3"] → ["AT.1", "AT.3"]  (siblings, both kept)
 */
const getDeepestCodes = (codes: string[]): string[] =>
  codes.filter((code) => !codes.some((other) => other !== code && other.startsWith(code + '.')));

/**
 * Collects admunitIds from active location filters for passing to the geo-widget.
 *
 * Sources:
 * 1. `locations` (autocomplete) — each ReferenceLocation may carry admunitIds directly
 * 2. `unitListValue` (country/region/district checkboxes) — looked up via filterData;
 *    only the deepest selected codes per branch are used to avoid superset overlap
 */
export const collectAdmunitIds = (
  locations: ReferenceLocation[],
  unitListValue: string[],
  filterData: FilterData | undefined,
): string[] => {
  const ids: string[] = [];

  for (const loc of locations) {
    if (loc.admunitIds?.length) {
      ids.push(...loc.admunitIds);
    }
  }

  if (unitListValue.length && filterData) {
    const deepest = getDeepestCodes(unitListValue);
    const allUnits = [
      ...(filterData.countries ?? []),
      ...(filterData.federalStates ?? []),
      ...(filterData.districts ?? []),
    ];

    for (const code of deepest) {
      const unit = allUnits.find((u) => u.code === code);
      if (unit?.admunitIds?.length) {
        ids.push(...unit.admunitIds);
      }
    }
  }

  return ids;
};

/**
 * Builds the filter patch to apply when switching to the MAP tab.
 * If derived admunitIds can be computed from current locations/unitListValue,
 * they are written into admunits and the location filters are cleared.
 * If admunits are already set (manual map selection), only tabs is switched.
 */
type BuildGoToMapPatchParams = {
  currentAdmunits?: FiltersType['admunits'];
  locations: ReferenceLocation[];
  unitListValue?: string[];
  filterData?: FilterData;
};

export const buildGoToMapPatch = ({
  currentAdmunits,
  locations,
  unitListValue = [],
  filterData,
}: BuildGoToMapPatchParams): Partial<FiltersType> => {
  if (currentAdmunits?.length) {
    return { tabs: 'MAP' as FiltersType['tabs'] };
  }
  const derivedIds = collectAdmunitIds(locations, unitListValue, filterData);
  if (derivedIds.length) {
    return {
      tabs: 'MAP' as FiltersType['tabs'],
      admunits: derivedIds.map((id) => ({ id, name: '', parent_name: '' })),
      locations: [],
      unitListValue: [],
      mapArea: undefined,
      currentPage: 0,
    };
  }
  return { tabs: 'MAP' as FiltersType['tabs'] };
};
